import React from 'react';
import { Link } from 'react-router-dom';
import { useMentoringHref } from '../mentoringLocale';

/* ------------------------------------------------------------------ links */

const isExternalHref = (href) => /^(https?:|mailto:|tel:)/.test(href);

/** Renders a react-router Link for internal paths, an anchor for external. */
export const SmartLink = ({ href, className, children, ...rest }) => {
  if (!href) {
    return children ?? null;
  }

  if (isExternalHref(href)) {
    return (
      <a
        href={href}
        className={className}
        target={href.startsWith('http') ? '_blank' : undefined}
        rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
        {...rest}
      >
        {children}
      </a>
    );
  }

  return (
    <Link to={href} className={className} {...rest}>
      {children}
    </Link>
  );
};

/** Button-style link that resolves its href from a content `target` key. */
export const ActionLink = ({ action, variant = 'primary', className = '', children, ...rest }) => {
  const href = useMentoringHref()(action);
  const tone = variant === 'on-dark' ? 'on-dark' : variant === 'ghost-dark' ? 'ghost-dark' : variant;
  const classes = `mt-btn mt-btn--${tone} ${className}`.trim();

  if (!href) {
    return null;
  }

  return (
    <SmartLink href={href} className={classes} {...rest}>
      {children ?? action?.label}
    </SmartLink>
  );
};

/** Underlined mono text link resolving from a content `target` key. */
export const ActionTextLink = ({ action, className = '', children, ...rest }) => {
  const href = useMentoringHref()(action);

  if (!href) {
    return null;
  }

  return (
    <SmartLink href={href} className={`mt-link ${className}`.trim()} {...rest}>
      {children ?? action?.label}
      <span aria-hidden="true">→</span>
    </SmartLink>
  );
};

/* --------------------------------------------------------------- section */

const TONE_CLASS = {
  default: '',
  paper2: 'mt-section--paper-2',
  ink: 'mt-section--ink',
  accent: 'mt-section--accent',
};

/**
 * A numbered content band. The [01] marker rides on the same line as the
 * eyebrow, above the title — the reference's section signature.
 */
export const Section = ({
  marker,
  eyebrow,
  title,
  kicker,
  lead,
  tone = 'default',
  flush = false,
  shell = 'mt-shell',
  className = '',
  children,
}) => {
  const toneClass = TONE_CLASS[tone] ?? '';
  const classes = [
    'mt-section',
    toneClass,
    flush ? 'mt-section--flush' : '',
    className,
  ].filter(Boolean).join(' ');
  const heading = kicker ?? title;

  return (
    <section className={classes}>
      <div className={shell}>
        {(marker || eyebrow || heading || lead) && (
          <div className="mt-section__head">
            {(marker || eyebrow) && (
              <div className="mt-section__topline">
                {marker && <span className="mt-section__marker">[{marker}]</span>}
                {eyebrow && <p className="mt-eyebrow">{eyebrow}</p>}
              </div>
            )}
            {heading && (
              <h2 className={kicker ? 'mt-title mt-title--sm' : 'mt-title'}>{heading}</h2>
            )}
            {lead && <p className="mt-lead mt-section__lead">{lead}</p>}
          </div>
        )}
        {children}
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ hero */

export const Hero = ({ ticker = [], eyebrow, titleLines = [], lead, leadExtra, primary, secondary, facts, figure, split = false }) => (
  <div className="mt-hero">
    <div className="mt-shell">
      {ticker.length > 0 && (
        <div className="mt-hero__ticker">
          {ticker.map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
      )}
      <div className={`mt-hero__grid${split ? ' mt-hero__grid--split' : ''}`}>
        <div>
          {eyebrow && <p className="mt-eyebrow">{eyebrow}</p>}
          <h1 className="mt-display mt-hero__title">
            {titleLines.map((line) => (
              <span className="mt-display__line" key={line}>{line}</span>
            ))}
          </h1>
        </div>
        {(lead || figure) && (
          <div className="mt-hero__aside">
            {lead && <p className="mt-lead">{lead}</p>}
            {leadExtra && <p className="mt-lead mt-hero__lead-extra">{leadExtra}</p>}
            {figure && <Figure label={figure.label} tall />}
          </div>
        )}
      </div>
      {(primary || secondary) && (
        <div className="mt-hero__actions">
          {primary && <ActionLink action={primary} />}
          {secondary && <ActionLink action={secondary} variant="ghost" />}
        </div>
      )}
      {facts && facts.length > 0 && (
        <div className="mt-hero__facts">
          <Facts items={facts} />
        </div>
      )}
    </div>
  </div>
);

/* ----------------------------------------------------------------- facts */

export const Facts = ({ items = [], stacked = false }) => (
  <dl className={`mt-facts${stacked ? ' mt-facts--stacked' : ''}`}>
    {items.map((item) => (
      <div className="mt-facts__item" key={`${item.label}-${item.value}`}>
        <dt>{item.label}</dt>
        <dd>{item.value}</dd>
      </div>
    ))}
  </dl>
);

/* ----------------------------------------------------------------- steps */

export const Steps = ({ items = [] }) => (
  <ol className="mt-steps">
    {items.map((item) => (
      <li className="mt-steps__item" key={item.number}>
        <span className="mt-steps__num">{item.number}</span>
        <div className="mt-steps__body">
          <h3 className="mt-steps__title">{item.title}</h3>
          {item.text && <p className="mt-steps__text">{item.text}</p>}
        </div>
      </li>
    ))}
  </ol>
);

/* -------------------------------------------------------------- lettered */

export const Lettered = ({ items = [] }) => (
  <div className="mt-lettered">
    {items.map((item) => (
      <div className="mt-lettered__item" key={item.key}>
        <span className="mt-lettered__key">[{item.key}]</span>
        <h3 className="mt-lettered__title">{item.title}</h3>
        <p className="mt-lettered__text">{item.text}</p>
      </div>
    ))}
  </div>
);

/* ----------------------------------------------------------------- cards */

export const CardGrid = ({ cols = 3, children }) => (
  <div className={`mt-grid mt-grid--${cols}`}>{children}</div>
);

const CardBody = ({ tag, title, text, action, state }) => (
  <>
    {tag && <span className="mt-card__tag">{tag}</span>}
    <h3 className="mt-card__title">{title}</h3>
    {text && <p className="mt-card__text">{text}</p>}
    {action && (
      <span className="mt-card__cta">
        {action.label}
        <span aria-hidden="true"> →</span>
      </span>
    )}
    {state && <span className="mt-card__state">{state}</span>}
  </>
);

export const Card = ({ tag, title, text, state, href, linkLabel }) => (
  href ? (
    <a className="mt-card mt-card--link" href={href} target="_blank" rel="noopener noreferrer">
      <CardBody tag={tag} title={title} text={text} state={state} action={{ label: linkLabel }} />
    </a>
  ) : (
    <div className="mt-card">
      <CardBody tag={tag} title={title} text={text} state={state} />
    </div>
  )
);

/** Card that resolves its own href from a content `target` key. */
export const ActionCard = ({ tag, title, text, action, state }) => {
  const href = useMentoringHref()(action);

  if (!href) {
    return null;
  }

  return (
    <SmartLink href={href} className="mt-card mt-card--link">
      <CardBody tag={tag} title={title} text={text} action={action} state={state} />
    </SmartLink>
  );
};

/* ---------------------------------------------------------------- figure */

export const Figure = ({ label, tall = false, accent = false }) => (
  <div
    className={[
      'mt-figure',
      tall ? 'mt-figure--tall' : '',
      accent ? 'mt-figure--accent' : '',
    ].filter(Boolean).join(' ')}
    role="img"
    aria-label={label}
  >
    <span className="mt-figure__label">{label}</span>
  </div>
);

export const FigureRow = ({ figures = [], tall = false }) => (
  <div className="mt-figures">
    {figures.map((figure) => (
      <Figure key={figure.label} label={figure.label} tall={tall} />
    ))}
  </div>
);

/* ----------------------------------------------------------------- stats */

export const Stats = ({ items = [], onDark = false }) => (
  <dl className={`mt-stats${onDark ? ' mt-stats--on-dark' : ''}`}>
    {items.map((item) => (
      <div className="mt-stats__item" key={`${item.value}-${item.unit}`}>
        <dd className="mt-stats__value">{item.value}</dd>
        <dt className="mt-stats__unit">{item.unit}</dt>
      </div>
    ))}
  </dl>
);

/* -------------------------------------------------------------- pipeline */

export const Pipeline = ({ steps = [], onDark = false }) => (
  <ol className={`mt-pipeline${onDark ? ' mt-pipeline--on-dark' : ''}`}>
    {steps.map((step) => (
      <li className="mt-pipeline__item" key={step.number}>
        <span className="mt-pipeline__num">{step.number}</span>
        <span className="mt-pipeline__title">{step.title}</span>
        <span className="mt-pipeline__text">{step.text}</span>
      </li>
    ))}
  </ol>
);

/* ---------------------------------------------------------------- marquee */

export const Marquee = ({ items = [], tone = 'accent' }) => {
  if (items.length === 0) {
    return null;
  }

  const track = (isClone) => (
    <div className="mt-marquee__track" key={isClone ? 'clone' : 'track'} aria-hidden={isClone || undefined}>
      {items.map((item) => (
        <React.Fragment key={item}>
          <span>{item}</span>
          <span className="mt-marquee__sep" aria-hidden="true">•</span>
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className={`mt-marquee${tone === 'ink' ? ' mt-marquee--ink' : ''}`}>
      {track(false)}
      {track(true)}
    </div>
  );
};

/* ------------------------------------------------------------------- faq */

export const Faq = ({ items = [] }) => (
  <div className="mt-faq">
    {items.map((item, index) => (
      <details className="mt-faq__item" key={item.question}>
        <summary>
          <span className="mt-faq__num">Q{String(index + 1).padStart(2, '0')}</span>
          <span>{item.question}</span>
          <span className="mt-faq__sign" aria-hidden="true">+</span>
        </summary>
        <p className="mt-faq__answer">{item.answer}</p>
      </details>
    ))}
  </div>
);

/* ------------------------------------------------------------------- cta */

export const CtaBlock = ({ kicker, title, text, primary, secondary, tone = 'accent' }) => (
  <div className={`mt-section mt-section--${tone}`}>
    <div className="mt-shell">
      {kicker && <p className="mt-eyebrow mt-cta__kicker">{kicker}</p>}
      <h2 className="mt-title">{title}</h2>
      {text && <p className="mt-lead mt-cta__text">{text}</p>}
      <div className="mt-cta__actions">
        {primary && <ActionLink action={primary} variant="on-dark" />}
        {secondary && <ActionLink action={secondary} variant="ghost-dark" />}
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ misc */

export const TagList = ({ items = [] }) => (
  <ul className="mt-tags">
    {items.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </ul>
);

export const CheckList = ({ items = [] }) => (
  <ul className="mt-checks">
    {items.map((item) => (
      <li key={typeof item === 'string' ? item : item.title}>
        <span>{typeof item === 'string' ? item : item.title}</span>
        {typeof item === 'string' ? null : <span className="mt-checks__text">{item.text}</span>}
      </li>
    ))}
  </ul>
);
